import type { ActionClient, ActionClientError } from "@adea/actions/client";
import { ERROR_STATUS } from "@adea/actions/spec";
import { getCsrfToken } from "./api.ts";
import { callLocale } from "./i18n.tsx";
import { lazyClient } from "./lazy-client.ts";
import { ensureSpecSoon, loadedSpec } from "./spec-loader.ts";
import type { WebSpecs } from "./specs.gen.ts";

/**
 * The web app's typed action client: one for the tab. Every read and change in the app goes through it, so
 * every call passes the registry's one gate. Types come from the specs (`specs.gen.ts` names their files).
 *
 * - `actions.useActionQuery(name, input)`: a read, cached in memory, refetched when the live channel says
 *   something it reads changed.
 * - `actions.useAction(name)`: a change. Its spec's instant update is applied at once and rolled back if the
 *   server refuses; afterwards everything it touched is refetched.
 * - When the gate asks for a confirmation, `askToConfirm` shows the dialog and the call goes on with the token.
 *
 * The specs are not part of the first load. A screen's actions arrive with the screen (`spec-loader.ts`: one
 * spec file at a time, the first time one of its actions is named), and until they have, the client works with a
 * stand-in: a call needs no spec, a read's live-channel refs are worked out when something changes (not when
 * the read is made), and an instant update simply waits for the next render. Every `*.specs.ts` in the web's
 * packages is found by name (`specs.gen.ts`); nothing is listed by hand.
 */
export type { WebSpecs };

/**
 * The API's own middleware (sign-in, CSRF, rate limits) answers before the gate, in the API's error shape
 * (`{ code, message }`), not the gate's (`{ ok: false, error }`). This turns the first into the second, so every
 * refusal reaches a screen the same way: a typed code and a message in the reader's language.
 */
export const gateFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.ok) return response;
  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as { ok?: boolean; code?: string; message?: string } | null;
  if (body && typeof body.ok === "boolean") return response;
  const code =
    body?.code && body.code in ERROR_STATUS
      ? body.code
      : response.status === 401
        ? "unauthenticated"
        : response.status === 403
          ? "forbidden"
          : response.status === 429
            ? "rate_limited"
            : "internal";
  const error = { code, message: body?.message ?? "" };
  return new Response(JSON.stringify({ ok: false, status: response.status, error }), {
    status: response.status,
    headers: { "content-type": "application/json" },
  });
};

/** Asks the client itself for the token (a late binding: the client is created just below). */
const confirmWith = (name: string, input: unknown, typed?: string): Promise<string> =>
  actions.confirm(name as keyof WebSpecs, input as never, typed);

export const actions: ActionClient<WebSpecs> = lazyClient<WebSpecs>(
  { loaded: loadedSpec, ensure: ensureSpecSoon },
  {
    fetch: gateFetch,
    headers: (): Record<string, string> => {
      const token = getCsrfToken();
      return token ? { "x-adea-csrf": token } : {};
    },
    locale: callLocale,
  },
  confirmWith,
);

/**
 * The same client for ⌘K: every call says it came from the palette (`x-adea-surface: command`), so the gate offers
 * only what's on the command surface and the audit shows where it was done. A click like any other.
 */
const confirmCommand = (name: string, input: unknown, typed?: string): Promise<string> =>
  commandActions.confirm(name as keyof WebSpecs, input as never, typed);

export const commandActions: ActionClient<WebSpecs> = lazyClient<WebSpecs>(
  { loaded: loadedSpec, ensure: ensureSpecSoon },
  {
    fetch: gateFetch,
    headers: (): Record<string, string> => {
      const token = getCsrfToken();
      return { "x-adea-surface": "command", ...(token ? { "x-adea-csrf": token } : {}) };
    },
    locale: callLocale,
  },
  confirmCommand,
);

export type { ActionClientError };

/** A refusal the person caused by cancelling a confirmation: nothing to say, nothing went wrong. */
export const wasCancelled = (error: unknown): boolean =>
  (error as ActionClientError | null)?.code === "cancelled";
